import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Checkbox, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography, ThemeProvider } from "@mui/material";
import JoditEditor from "jodit-react";
import { useSchedulingOrg } from "../../../hooks/ReactQueryHooks/useSchedulingOrg";
import OrganizationSelect from "../Scheduling/OrganizationSelect";
import { schedulingTheme } from "../Scheduling/ui/SchedulingTheme";
import { notificationSettings, saveNotificationSetting, resetNotificationSetting } from "../../api/Scheduling/notifications";
import { schedulingErrorMessage } from "../../api/Scheduling/errors";
import { groupNotificationSettings, notificationAudienceLabels, notificationChannelLabels, notificationSettingDirty } from "./notificationModel";

function channelLabel(setting) {
  if (!setting.enabled) return `${notificationAudienceLabels[setting.audience]}: Disabled`;
  return `${notificationAudienceLabels[setting.audience]}: ${setting.channels.map(channel => notificationChannelLabels[channel]).join(", ")}`;
}

function TemplateEditor({ settings, organizationRef, placeholders, onClose, onSaved }) {
  const [audience, setAudience] = useState(settings[0].audience);
  const [drafts, setDrafts] = useState(() => Object.fromEntries(settings.map(item => [item.audience, item])));
  const [baseline, setBaseline] = useState(() => Object.fromEntries(settings.map(item => [item.audience, item])));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const draft = drafts[audience];
  const current = settings.find(item => item.audience === audience) || settings[0];
  const dirtyItems = settings.filter(item => notificationSettingDirty(drafts[item.audience], baseline[item.audience]));
  const config = useMemo(() => ({ readonly: busy, toolbarAdaptive: false, buttons: "bold,italic,underline,ul,ol,link", showCharsCounter: false, showWordsCounter: false, showXPathInStatusbar: false, height: 230 }), [busy]);
  const change = (key, value) => setDrafts(previous => ({ ...previous, [audience]: { ...previous[audience], [key]: value } }));
  const remember = saved => {
    setDrafts(previous => ({ ...previous, [saved.audience]: saved }));
    setBaseline(previous => ({ ...previous, [saved.audience]: saved }));
  };
  const save = async (reset = false) => {
    setBusy(true); setError("");
    const stored = [];
    try {
      if (reset) {
        await resetNotificationSetting(organizationRef, drafts[audience]);
        const latest = ((await notificationSettings(organizationRef)).settings || []).find(item => item.eventType === current.eventType && item.audience === audience);
        if (latest) remember(latest);
        await onSaved();
        if (!dirtyItems.some(item => item.audience !== audience)) onClose();
      } else {
        for (const item of dirtyItems) {
          const result = await saveNotificationSetting(organizationRef, drafts[item.audience]);
          stored.push(result.setting);
          remember(result.setting);
        }
        await onSaved();
        onClose();
      }
    } catch (failure) {
      stored.forEach(remember);
      setError(schedulingErrorMessage(failure));
    } finally { setBusy(false); }
  };
  return <Dialog open fullWidth maxWidth="md" onClose={busy ? undefined : onClose} aria-labelledby="notification-editor-title">
    <DialogTitle id="notification-editor-title">{current.label}</DialogTitle>
    <DialogContent dividers sx={{ "& input": { height: "auto", minHeight: 0 }, "& textarea": { height: "auto" } }}>
      <Stack spacing={2}>
        {error && <Alert severity="error">{error}</Alert>}
        <Typography variant="body2" color="text.secondary">{current.timing}.</Typography>
        {settings.length > 1
          ? <ToggleButtonGroup exclusive size="small" value={audience} onChange={(_, value) => { if (value) setAudience(value); }} aria-label="Notification recipient"
              sx={{ "& .MuiToggleButton-root": { textTransform: "none", px: 2 }, "& .MuiToggleButton-root.Mui-selected": { color: "primary.main", bgcolor: "#e8f2fb" } }}>
              {settings.map(item => <ToggleButton key={item.audience} value={item.audience}>
                {notificationAudienceLabels[item.audience]}
                {dirtyItems.some(dirty => dirty.audience === item.audience) && <Box aria-hidden sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "primary.main", ml: 1 }} />}
              </ToggleButton>)}
            </ToggleButtonGroup>
          : <Chip size="small" label={notificationAudienceLabels[current.audience]} />}
        <Typography variant="body2">{draft.recipients}</Typography>
        <FormControlLabel control={<Switch checked={draft.enabled} disabled={busy} onChange={(_, value) => change("enabled", value)} />} label="Enabled" />
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Choose where they get notified</Typography>
          <Stack direction="row" flexWrap="wrap" aria-label="Delivery channels">
            {Object.entries(notificationChannelLabels).map(([value, label]) => <FormControlLabel key={value} label={label} control={<Checkbox checked={draft.channels.includes(value)} disabled={busy || current.eventType === "guest_invited"} onChange={(_, checked) => change("channels", checked ? [...draft.channels, value] : draft.channels.filter(item => item !== value))} />} />)}
          </Stack>
        </Box>
        <TextField label="Title" value={draft.title} disabled={busy} onChange={e => change("title", e.target.value)} inputProps={{ maxLength: 150 }} />
        <TextField label="Message" multiline minRows={3} value={draft.content} disabled={busy} onChange={e => change("content", e.target.value)} inputProps={{ maxLength: 2000 }} />
        <TextField label="Email subject" value={draft.emailSubject} disabled={busy} onChange={e => change("emailSubject", e.target.value)} inputProps={{ maxLength: 200 }} />
        <Box><Typography component="label" htmlFor="scheduling-email-template" variant="body2">Email message</Typography>
          <JoditEditor key={audience} id="scheduling-email-template" value={draft.emailContent} config={config} onBlur={value => change("emailContent", value)} />
        </Box>
        <Typography variant="caption" color="text.secondary">Available placeholders: {placeholders.map(item => `[${item}]`).join(", ")}</Typography>
        <Box sx={{ p: 2, borderRadius: 2, bgcolor: "background.default", border: "1px solid", borderColor: "divider" }} aria-label="Message preview">
          <Typography variant="caption" color="text.secondary">Preview</Typography>
          <Typography fontWeight={600}>{draft.title}</Typography>
          <Typography variant="body2">{draft.content}</Typography>
        </Box>
      </Stack>
    </DialogContent>
    <DialogActions sx={{ flexWrap: "wrap", gap: 1, p: 2 }}>
      {!baseline[audience]?.isDefault && <Button disabled={busy} onClick={() => save(true)}>Restore defaults</Button>}
      <Box sx={{ flex: 1 }} /><Button disabled={busy} onClick={onClose}>Cancel</Button>
      <Button variant="contained" disabled={busy || !dirtyItems.length || dirtyItems.some(item => drafts[item.audience].enabled && !drafts[item.audience].channels.length)} onClick={() => save()}>{busy ? "Saving…" : "Save changes"}</Button>
    </DialogActions>
  </Dialog>;
}

export default function SchedulingNotifications() {
  const org = useSchedulingOrg();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null);
  const key = ["scheduling-notification-settings", org.user?.sub, org.organizationRef];
  const query = useQuery(key, () => notificationSettings(org.organizationRef), { enabled: !!org.organizationRef, refetchOnWindowFocus: false });
  const groups = groupNotificationSettings(query.data?.settings);
  return <ThemeProvider theme={schedulingTheme}><Box sx={{ p: { xs: 1, md: 3 } }}>
    <Stack spacing={3}>
      <Box>
        <Typography variant="h6">Scheduling notifications</Typography>
        <Typography color="text.secondary">Choose delivery channels and write separate patient and practitioner messages for this organization.</Typography>
      </Box>
      <Box sx={{ maxWidth: 420 }}><OrganizationSelect context={org} inputId="notification-organization" disabled={!!editing} /></Box>
      {query.isLoading && <CircularProgress aria-label="Loading scheduling notifications" />}
      {query.isError && <Alert severity="error" action={<Button onClick={() => query.refetch()}>Retry</Button>}>{schedulingErrorMessage(query.error, "Could not load notification settings.")}</Alert>}
      {groups.map(group => <Box key={group.eventType} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2 }}>
        <Box sx={{ flex: "1 1 300px" }}>
          <Typography fontWeight={600}>{group.label}</Typography>
          <Typography variant="body2" color="text.secondary">{group.settings.length > 1 ? group.settings.map(item => notificationAudienceLabels[item.audience]).join(" and ") : group.settings[0].recipients}</Typography>
          <Typography variant="caption">{group.timing}</Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap">{group.settings.map(setting => <Chip key={setting.audience} size="small" label={channelLabel(setting)} />)}</Stack>
        <Button variant="outlined" aria-label={`Edit ${group.label}`} onClick={() => setEditing({ settings: group.settings, organizationRef: org.organizationRef })}>Edit</Button>
      </Box>)}
    </Stack>
    {editing && <TemplateEditor key={`${editing.organizationRef}:${editing.settings[0].eventType}`} {...editing} placeholders={query.data?.placeholders || []} onClose={() => setEditing(null)} onSaved={() => queryClient.invalidateQueries(key)} />}
  </Box></ThemeProvider>;
}
